import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import {
  getDisplayableTiers,
  formatShippingTierLabel,
  formatTierQuantityLabel,
  formatDiscountCapLabel,
  formatTierThreshold,
  useGetProductTiers
} from '@cannect/services/resources/progressive-discounts'

interface ProgressiveDiscountBadgeProps {
  productId: number
  /** Classe CSS adicional para ajuste de posicionamento no contexto do pai. */
  className?: string
}

/**
 * Badge discreto exibindo a melhor faixa de desconto progressivo do produto.
 * Renderizado apenas para itens fora de grupos (lista normal do carrinho).
 * Retorna null se o produto não tiver desconto progressivo, não houver faixas,
 * ou se ESTE produto especificamente estiver coberto por um cupom aplicado (o backend suprime o
 * progressivo só nos itens que o cupom desconta — os demais produtos mantêm o badge normalmente).
 */
export function ProgressiveDiscountBadge({ productId, className }: ProgressiveDiscountBadgeProps) {
  const { coupon, lastOrderCreated } = usePrepareCart()
  const { data: tiersData } = useGetProductTiers(productId)

  // item.discount_type (itemOutputDTO): 'progressive' = manteve o progressivo; qualquer outro valor
  // truthy (ex.: 'coupon') = esse item foi coberto pelo cupom.
  const cartItem = lastOrderCreated?.items?.find(
    (ci: any) => Number(ci?.product_id || ci?.product?.id || ci?.id) === productId
  )
  const coveredByCoupon = !!coupon && !!cartItem?.discount_type && cartItem.discount_type !== 'progressive'
  if (coveredByCoupon) return null
  if (!tiersData?.has_progressive_discount) return null

  // Regra SÓ de frete: discount_percent é sempre 0 (item não baixa de preço) — faixa exibível vem de shipping_value
  const isShipping = tiersData.rule?.benefit_target === 'shipping'
  const displayable = isShipping
    ? (tiersData.tiers ?? []).filter((tier) => Number(tier.shipping_value) > 0)
    : getDisplayableTiers(tiersData.tiers)

  // Frete vindo de OUTRA regra (o produto também tem regra de item): segundo chip, nunca mesclado ao do item
  const shippingRule = tiersData.shipping_rule
  const shippingTiers = (tiersData.shipping_tiers ?? []).filter((tier) => Number(tier.shipping_value) > 0)
  const lastShippingTier = shippingRule ? shippingTiers[shippingTiers.length - 1] : undefined
  const shippingExtraLabel = lastShippingTier
    ? `${formatShippingTierLabel(lastShippingTier)} a partir de ${formatTierThreshold(lastShippingTier, shippingRule?.trigger_type, { brandScope: shippingRule?.condition_scope === 'targeted' })}.`
    : undefined

  if (!displayable.length && !shippingExtraLabel) return null

  // Regra 'both': faixa de 0% no produto (só frete) não vira "0% OFF" — usa a última faixa com desconto de item
  const itemTiers = displayable.filter((tier) => Number(tier.discount_percent) > 0)
  const itemTierToShow =
    isShipping || !itemTiers.length ? displayable[displayable.length - 1] : itemTiers[itemTiers.length - 1]
  const last = displayable.length ? itemTierToShow : undefined
  const maxDiscountedUnits = Number(tiersData.rule?.max_discounted_units) || 0
  const triggerType = tiersData.rule?.trigger_type
  const isBrandScoped = tiersData.rule?.condition_scope === 'targeted'
  let label = ''
  if (last && isShipping) {
    label = `${formatShippingTierLabel(last)} a partir de ${formatTierThreshold(last, triggerType, { brandScope: isBrandScoped })}.`
  } else if (last) {
    label = `${last.discount_percent}% OFF em ${formatTierQuantityLabel(last, triggerType, { capped: maxDiscountedUnits > 0 })}`
  }

  const baseClass =
    'w-fit self-start rounded-md bg-warning-100 px-2 py-[1px] text-[11px] italic text-neutral-600 shadow-sm'

  const chipClass = className ? `${baseClass} ${className}` : baseClass

  return (
    <>
      {!!label && (
        <span className={chipClass}>
          {label}
          {!isShipping && maxDiscountedUnits > 0 && (
            <>
              {' '}
              (<strong>{formatDiscountCapLabel(maxDiscountedUnits)}</strong>)
            </>
          )}
        </span>
      )}
      {!!shippingExtraLabel && <span className={chipClass}>{shippingExtraLabel}</span>}
    </>
  )
}
